import { api } from './client';
import type { Difficulty } from './puzzles';
import type { ClaimLifeInput } from '../game/local-persistence';

const ANON_ID_KEY = 'sd-anon-id';

/** A stable random id for an unregistered visitor. No PII. */
export function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    // Storage disabled/full — fall back to an ephemeral id (counts as a new guest).
    return crypto.randomUUID();
  }
}

/**
 * Fire-and-forget guest play beacon. Never throws and never blocks gameplay —
 * analytics must not break the game. The server insert-ignores duplicates, so
 * re-firing the same (id, date, difficulty, life) is harmless.
 */
export async function recordAnonPlay(
  date: string,
  difficulty: Difficulty,
  lifeNumber: number,
): Promise<void> {
  try {
    await api.post('/api/anon/plays', {
      anon_id: getAnonId(),
      puzzle_date: date,
      difficulty,
      life_number: lifeNumber,
    });
  } catch {
    // swallow — best-effort analytics
  }
}

const BOARD_KEY = 'sd-guest-board:';
const CLAIMED_KEY = 'sd-guest-claimed:';

/**
 * Has this browser's guest run for `date` already been handed to an account?
 * Once it has, the local save is a duplicate of data that now lives on the
 * account, so it must not be shown as live guest progress — and it must not
 * be claimed a second time.
 */
export function isDayClaimed(date: string): boolean {
  try {
    return localStorage.getItem(CLAIMED_KEY + date) !== null;
  } catch {
    return false;
  }
}

export function markDayClaimed(date: string): void {
  try {
    localStorage.setItem(CLAIMED_KEY + date, '1');
  } catch {
    // Storage disabled — worst case the claim is attempted again and the
    // server answers ALREADY_PLAYED.
  }
}

/** Local mirror of the entitlement so the landing page can paint locks at once. */
export function readLocalBoards(date: string): Difficulty[] {
  try {
    const raw = localStorage.getItem(BOARD_KEY + date);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as Difficulty[]) : [];
  } catch {
    return [];
  }
}

export function saveLocalBoards(date: string, difficulties: Difficulty[]): void {
  try {
    localStorage.setItem(BOARD_KEY + date, JSON.stringify(difficulties));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(BOARD_KEY) && key !== BOARD_KEY + date) localStorage.removeItem(key);
    }
  } catch {
    // Storage disabled — the cookie is still the authority.
  }
}

/**
 * Commit the guest to a board. Retries once, then FAILS OPEN: an unreachable
 * server must never stop someone playing. Returns the unlocked difficulties,
 * or [] if the call never landed.
 */
export async function claimBoard(date: string, difficulty: Difficulty): Promise<Difficulty[]> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await api.post('/api/anon/board', { difficulty });
      const unlocked = (res.data.unlocked ?? []) as Difficulty[];
      saveLocalBoards(date, unlocked);
      return unlocked;
    } catch {
      // fall through to the retry, then give up
    }
  }
  return [];
}

export interface ClaimResponse {
  claimed: boolean;
  best_score?: number;
  won?: boolean;
  rank?: number | null;
  reason?: 'NOT_TODAY' | 'NO_PUZZLE' | 'ALREADY_PLAYED';
}

export async function claimGuestRun(payload: {
  date: string;
  difficulty: Difficulty;
  lives: ClaimLifeInput[];
}): Promise<ClaimResponse> {
  const res = await api.post('/api/anon/claim', { anon_id: getAnonId(), ...payload });
  return res.data;
}
